import React, { useState } from 'react';
import styled from 'styled-components';
import {
  CreateVenueBulkItemResult,
  VenueBulkResultStatus,
} from '../../../shared/dto/EntityRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import { useAdminVenue } from '../../share/hook/useAdminVenue';

const STATUS_LABEL: Record<VenueBulkResultStatus, string> = {
  CREATED: 'Created',
  SKIPPED_DUPLICATE: 'Skipped',
  CONFLICT: 'Conflict',
  INVALID: 'Invalid',
};

const VenueBulkResult: React.FC = () => {
  const { bulkResult, setBulkResult, selectVenueById } = useAdminVenue();
  const [issuesOnly, setIssuesOnly] = useState(false);
  if (!bulkResult) return null;

  const rows = issuesOnly
    ? bulkResult.results.filter((item) =>
      item.status === 'CONFLICT' || item.status === 'INVALID')
    : bulkResult.results;

  const handleRowClick = (item: CreateVenueBulkItemResult) => {
    if (item.venueId == null) return;
    selectVenueById(String(item.venueId));
  };

  return (
    <ResultContainer>
      <Summary>
        {bulkResult.createdCount} created / {bulkResult.duplicateCount} skipped / {bulkResult.conflictCount} conflict / {bulkResult.invalidCount} error
      </Summary>
      <FilterBar>
        <HeadlessBtn
          value="All"
          handleClick={() => setIssuesOnly(false)}
          StyledBtn={!issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
        <HeadlessBtn
          value={`Issues (${bulkResult.conflictCount + bulkResult.invalidCount})`}
          handleClick={() => setIssuesOnly(true)}
          StyledBtn={issuesOnly ? FilterBtnActive : FilterBtnInactive}
        />
      </FilterBar>
      <ResultList>
        {rows.map((item) => (
          <ResultRow
            key={`${item.rowNumber}-${item.venueCode ?? ''}`}
            $clickable={item.venueId != null}
            onClick={() => handleRowClick(item)}
          >
            <RowMeta>
              Row {item.rowNumber}
              {item.venueCode ? ` · ${item.venueCode}` : ''}
            </RowMeta>
            <Status $issue={item.status === 'CONFLICT' || item.status === 'INVALID'}>
              {STATUS_LABEL[item.status]}
            </Status>
            {item.message && <Message>{item.message}</Message>}
          </ResultRow>
        ))}
      </ResultList>
      <BtnContainer>
        <HeadlessBtn
          value="Done"
          handleClick={() => setBulkResult(null)}
          StyledBtn={PaperBlurFitBtn}
        />
      </BtnContainer>
    </ResultContainer>
  );
};

const ResultContainer = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 40px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Summary = styled.div`
  padding: 8px 0 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const FilterBtnActive = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const FilterBtnInactive = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const ResultRow = styled.article<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $issue: boolean }>`
  color: ${({ theme, $issue }) =>
    $issue ? theme.colors.color_Gray_02 : theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  word-break: break-word;
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
`;

export default VenueBulkResult;
